import { useEffect, useState } from 'react';
import { Link, type Location } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ComicSource, PagePreview, PagePreviewBatch, SourceAuth } from '@/sources/types';

interface GalleryPreviewsProps {
  source: ComicSource;
  comicId: string;
  initialBatch?: PagePreviewBatch;
  auth?: SourceAuth;
  locationState?: Location['state'];
}

export function GalleryPreviews({
  source,
  comicId,
  initialBatch,
  auth,
  locationState,
}: GalleryPreviewsProps) {
  const [batches, setBatches] = useState<PagePreviewBatch[]>(() =>
    initialBatch && initialBatch.items.length > 0 ? [initialBatch] : [],
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setBatches(initialBatch && initialBatch.items.length > 0 ? [initialBatch] : []);
    setError(null);
  }, [comicId, initialBatch]);

  if (!initialBatch || initialBatch.items.length === 0) return null;

  const previews = batches.flatMap((batch) => batch.items);
  const lastBatch = batches[batches.length - 1] ?? initialBatch;
  const loadedCount = previews.length;
  const total = lastBatch.totalItems ?? initialBatch.totalItems;
  const canLoadMore = Boolean(source.getPagePreviews && lastBatch.hasMore);

  async function handleLoadMore() {
    if (!source.getPagePreviews || loadingMore || !lastBatch.nextPreviewPage) return;
    setLoadingMore(true);
    setError(null);
    try {
      const nextBatch = await source.getPagePreviews(comicId, auth, {
        previewPage: lastBatch.nextPreviewPage,
      });
      setBatches((current) => [...current, nextBatch]);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Couldn't load more previews: ${message}`);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <section className="flex flex-col gap-3" aria-labelledby="gallery-previews-heading">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="gallery-previews-heading" className="text-lg font-semibold">
            Page previews
          </h2>
          <p className="text-sm text-muted-foreground">
            {total ? `Showing ${loadedCount} of ${total}` : `${loadedCount} loaded`}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8">
        {previews.map((preview) => (
          <PreviewLink
            key={`${preview.page}:${preview.extra?.ptoken ?? preview.thumbnail.url}`}
            sourceId={source.id}
            comicId={comicId}
            preview={preview}
            locationState={locationState}
          />
        ))}
      </div>
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {canLoadMore && (
        <div>
          <Button type="button" variant="outline" onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading previews...' : 'Load more previews'}
          </Button>
        </div>
      )}
    </section>
  );
}

function PreviewLink({
  sourceId,
  comicId,
  preview,
  locationState,
}: {
  sourceId: string;
  comicId: string;
  preview: PagePreview;
  locationState?: Location['state'];
}) {
  return (
    <Link
      to={`/source/${sourceId}/comic/${encodeURIComponent(comicId)}/read/${preview.page}`}
      state={locationState}
      className="group rounded-md border border-border/60 bg-card p-1 transition hover:border-primary/60 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      aria-label={`Open page ${preview.page}`}
    >
      <PreviewThumb preview={preview} />
      <span className="mt-1 block truncate px-1 text-center text-xs text-muted-foreground group-hover:text-foreground">
        {preview.page}
      </span>
    </Link>
  );
}

function PreviewThumb({ preview }: { preview: PagePreview }) {
  const commonClass = 'aspect-[2/3] w-full overflow-hidden rounded bg-muted';
  const thumbnail = preview.thumbnail;
  if (thumbnail.kind === 'image') {
    return (
      <div className={commonClass}>
        <img
          src={thumbnail.url}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  return (
    <div className={cn(commonClass, 'flex items-center justify-center')}>
      <div
        aria-hidden="true"
        style={{
          width: thumbnail.width,
          height: thumbnail.height,
          maxWidth: '100%',
          maxHeight: '100%',
          backgroundImage: `url("${thumbnail.url}")`,
          backgroundPosition: `-${thumbnail.x}px -${thumbnail.y}px`,
          backgroundRepeat: 'no-repeat',
        }}
      />
    </div>
  );
}
